import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { driverService } from '@services/driverService';
import { Driver } from '@/types/driver';

export const DriverListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await driverService.getAll({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = async (id: number) => {
    try {
      await driverService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Motoristas</h1>
          <p className="text-slate-600">Gerencie os motoristas cadastrados no sistema</p>
        </div>
        <PermissionGuard permission="drivers.write">
          <Button onClick={() => navigate('/settings/transport/drivers/new')} icon={<Plus size={20} />}>
            Novo Motorista
          </Button>
        </PermissionGuard>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por nome ou e-mail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Button onClick={handleSearch} variant="secondary" icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Nome</th>
                <th className="text-left">E-mail</th>
                <th className="text-left">WhatsApp</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Nenhum motorista encontrado
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.driver_id}>
                    <TableCell className="font-mono text-sm">{item.driver_id}</TableCell>
                    <TableCell className="font-medium">{item.full_name}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.whatsapp_phone_number}</TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? 'success' : 'neutral'}>
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="drivers.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/transport/drivers/${item.driver_id}/edit`)}
                            icon={<Edit size={16} />}
                          />
                        </PermissionGuard>
                        <PermissionGuard permission="drivers.write">
                          {deleteConfirm === item.driver_id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.driver_id)}>
                                Confirmar
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.driver_id)}
                              icon={<Trash2 size={16} className="text-red-500" />}
                            />
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {totalItems} registro(s) encontrado(s)
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
